import { formatDistanceToNowStrict, parseISO } from "date-fns";
import {
  Clock3,
  Loader2,
  Pause,
  PencilLine,
  Play,
  Plus,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../../../components/ui/badge";
import { Button, buttonVariants } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import {
  queryClient,
  useWorkspaceSkills,
  useToggleSkill,
  useDeleteSkill,
} from "../../../../hooks/QueryHooks";
import { cn } from "../../../../lib/utils";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";
import type { CustomSkill } from "../../../../services/skills/skillsEndpoints.interface";
import useSkillsStore from "../../../../store/skills/skillsStore";

const kindLabel: Record<string, string> = {
  capability: "Capability",
  playbook: "Playbook",
};

const formatRelativeTime = (value?: string) => {
  if (!value) return "just now";
  try {
    return formatDistanceToNowStrict(parseISO(value), { addSuffix: true });
  } catch {
    return value;
  }
};

const SkillsListPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const navigate = useNavigate();
  const listSearch = useSkillsStore((s) => s.listSearch);
  const setListSearch = useSkillsStore((s) => s.setListSearch);
  const listStatusFilter = useSkillsStore((s) => s.listStatusFilter);
  const setListStatusFilter = useSkillsStore((s) => s.setListStatusFilter);

  const { data: skills, isLoading }: any = useWorkspaceSkills({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const toggleSkillMutation = useToggleSkill();
  const deleteSkillMutation = useDeleteSkill();
  const [skillToDelete, setSkillToDelete] = useState<CustomSkill | null>(null);

  const filteredSkills = useMemo(() => {
    const source: CustomSkill[] = Array.isArray(skills) ? skills : [];
    return source
      .filter((skill) => skill.visibility !== "protected")
      .filter((skill) => {
        if (listStatusFilter === "all") return true;
        return skill.status === listStatusFilter;
      })
      .filter((skill) => {
        const term = listSearch.trim().toLowerCase();
        if (!term) return true;
        return `${skill.skill_name} ${skill.display_name} ${skill.description}`
          .toLowerCase()
          .includes(term);
      })
      .sort((a, b) => {
        const aDate = Date.parse(a.updated_at || a.created_at || "") || 0;
        const bDate = Date.parse(b.updated_at || b.created_at || "") || 0;
        return bDate - aDate;
      });
  }, [skills, listStatusFilter, listSearch]);

  const handleToggle = (skill: CustomSkill) => {
    toggleSkillMutation.mutate(
      {
        workspace_id: workspace?.id,
        skill_id: skill.id,
        enabled: !skill.is_enabled,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["skills", workspace?.id],
          });
          notify({
            type: "success",
            messageList: [
              `Skill ${skill.is_enabled ? "disabled" : "enabled"} successfully.`,
            ],
          });
        },
        onError: () => {
          notify({
            type: "failure",
            messageList: ["Failed to toggle skill."],
          });
        },
      },
    );
  };

  const handleDelete = () => {
    if (!skillToDelete) return;
    deleteSkillMutation.mutate(
      { workspace_id: workspace?.id, skill_id: skillToDelete.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["skills", workspace?.id],
          });
          notify({
            type: "success",
            messageList: ["Skill deleted."],
          });
          setSkillToDelete(null);
        },
        onError: () => {
          notify({
            type: "failure",
            messageList: ["Failed to delete skill."],
          });
        },
      },
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-muted p-2">
            <Sparkles className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">Skills</h1>
            <p className="text-sm text-muted-foreground">
              Teach your AI agent custom capabilities and playbooks
            </p>
          </div>
        </div>
        <Button onClick={() => navigate("new")}>
          <Plus className="mr-1.5 h-4 w-4" />
          New Skill
        </Button>
      </div>

      {/* Filter bar */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <Tabs
          value={listStatusFilter}
          onValueChange={(v) =>
            setListStatusFilter(v as typeof listStatusFilter)
          }
        >
          <TabsList>
            <TabsTrigger value="all">All Skills</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="draft">Draft</TabsTrigger>
            <TabsTrigger value="archived">Archived</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={listSearch}
            onChange={(e) => setListSearch(e.target.value)}
            placeholder="Search skills..."
            className="pl-9"
          />
        </div>
      </div>

      {/* Skill cards */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : filteredSkills.length ? (
          <div className="mx-auto max-w-4xl space-y-4 p-6">
            {filteredSkills.map((skill) => {
              const isDisabled = !skill.is_enabled;

              return (
                <div
                  key={skill.id}
                  className="group cursor-pointer rounded-lg border border-border bg-background p-5 transition-all hover:border-foreground/20"
                  onClick={() => navigate(`${skill.id}`)}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="rounded-lg bg-muted p-2.5">
                      <Sparkles className="h-5 w-5 text-muted-foreground" />
                    </div>

                    {/* Content — middle column */}
                    <div className="min-w-0 flex-1">
                      <h3 className="mb-1 font-medium text-foreground transition-colors group-hover:text-primary">
                        {skill.display_name || skill.skill_name}
                      </h3>
                      <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                        {skill.description || "No description"}
                      </p>

                      {/* Footer left: kind badge + status */}
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">
                          {kindLabel[skill.kind] || skill.kind}
                        </Badge>

                        <div className="flex items-center gap-1.5">
                          {isDisabled ? (
                            <Pause className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <div className="h-2 w-2 rounded-full bg-foreground" />
                          )}
                          <span className="text-xs capitalize text-muted-foreground">
                            {isDisabled ? "disabled" : skill.status}
                          </span>
                        </div>

                        <span className="text-xs text-muted-foreground">
                          v{skill.version}
                        </span>
                      </div>
                    </div>

                    {/* Right column: hover actions on top, meta on bottom */}
                    <div className="flex shrink-0 flex-col items-end justify-between self-stretch">
                      {/* Hover action buttons */}
                      <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          title="Edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`${skill.id}`);
                          }}
                        >
                          <PencilLine className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          title={isDisabled ? "Enable" : "Disable"}
                          disabled={toggleSkillMutation.isLoading}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(skill);
                          }}
                        >
                          {isDisabled ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          title="Delete"
                          disabled={deleteSkillMutation.isLoading}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSkillToDelete(skill);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Time — bottom right */}
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock3 className="h-3 w-3" />
                        <span>{formatRelativeTime(skill.updated_at || skill.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="mb-4 rounded-full bg-muted p-4">
              <Sparkles className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-lg font-medium text-foreground">No skills found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {listSearch || listStatusFilter !== "all"
                ? "Try changing filters or search criteria."
                : "Create your first skill to get started."}
            </p>
            {!listSearch && listStatusFilter === "all" && (
              <Button onClick={() => navigate("new")} className="mt-4">
                <Plus className="mr-1.5 h-4 w-4" />
                New Skill
              </Button>
            )}
          </div>
        )}
      </div>
      <AlertDialog
        open={!!skillToDelete}
        onOpenChange={(open) => {
          if (!open) setSkillToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Skill</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{skillToDelete?.display_name || skillToDelete?.skill_name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "default" })}
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SkillsListPage;
